import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { MarketingNav } from "../components/Nav";
import Footer from "../components/Footer";

const sections = [
  {
    id: "acceptance",
    title: "Acceptance of terms",
    body: `By creating a Bora Moyo account or using any part of our service, you agree to be bound by these Terms of Service. If you do not agree, do not use the service. We may update these terms from time to time — continued use after changes constitutes acceptance.`,
  },
  {
    id: "eligibility",
    title: "Eligibility",
    body: `You must be at least 18 years of age to use Bora Moyo. By using the service, you represent that you are 18 or older. We reserve the right to terminate accounts of users found to be under 18.`,
  },
  {
    id: "account",
    title: "Your account",
    body: `You are responsible for maintaining the confidentiality of your login credentials and for all activity that occurs under your account. You agree to notify us immediately of any unauthorized use. You may not transfer your account to another person. Each person may hold one active account.`,
  },
  {
    id: "conduct",
    title: "Acceptable use",
    body: `You agree not to:
• Provide false information on your profile.
• Harass, abuse, threaten, or harm other users.
• Use the platform for commercial solicitation, spam, or scams.
• Attempt to access other users' accounts or data.
• Use automated tools, bots, or scrapers to interact with the service.
• Impersonate another person or entity.
• Post content that is illegal, obscene, defamatory, or infringes any third-party rights.

Violations may result in immediate account termination.`,
  },
  {
    id: "content",
    title: "User content",
    body: `You retain ownership of content you post to Bora Moyo. By posting content, you grant us a non-exclusive, royalty-free, worldwide license to use, display, and distribute that content solely for operating and improving the service. You are responsible for ensuring your content does not violate any third-party rights.`,
  },
  {
    id: "membership",
    title: "Paid membership",
    body: `Some features require a paid subscription. Subscriptions are billed in advance on a monthly basis. You may cancel anytime; cancellation takes effect at the end of the current billing period. We do not offer refunds for partial months. In the event of significant service disruption, we may issue credits at our discretion.`,
  },
  {
    id: "termination",
    title: "Termination",
    body: `You may delete your account at any time from Settings. We reserve the right to suspend or terminate any account that violates these terms, our community guidelines, or applicable law — without prior notice in cases of serious violations. Upon termination, your access to the service ends immediately.`,
  },
  {
    id: "disclaimer",
    title: "Disclaimers",
    body: `Bora Moyo is provided "as is" without warranties of any kind. We do not guarantee that you will find a match, or that matches will meet your expectations. We are not responsible for the conduct of users on or off the platform. We do not conduct criminal background checks.`,
  },
  {
    id: "liability",
    title: "Limitation of liability",
    body: `To the maximum extent permitted by law, Bora Moyo shall not be liable for indirect, incidental, special, consequential, or punitive damages arising from your use of the service. Our total liability for any claim arising from these terms shall not exceed the amount you paid us in the three months preceding the claim.`,
  },
  {
    id: "governing",
    title: "Governing law",
    body: `These terms are governed by applicable law. Any disputes will be resolved through binding arbitration, except where prohibited by law. You agree to resolve disputes individually and not as part of a class action.`,
  },
  {
    id: "contact",
    title: "Contact",
    body: `For questions about these terms:\nEmail: legal@boramoyo.com\nLast updated: April 2026`,
  },
];

export default function Terms() {
  useEffect(() => {
    document.title = "Terms of Service — Bora Moyo";
  }, []);

  return (
    <div className="bg-[#05070d] min-h-screen text-white">
      <MarketingNav />

      <section className="pt-40 pb-16 md:pt-52 px-6 md:px-12">
        <div className="mx-auto max-w-3xl">
          <div className="text-[11px] tracking-[0.28em] uppercase text-[#D4AF37] mb-4">Legal</div>
          <h1 className="font-display text-4xl md:text-6xl leading-[1.04] tracking-tight mb-6">Terms of Service</h1>
          <p className="text-white/50 text-sm">Last updated: April 2026 &middot; Effective immediately</p>
        </div>
      </section>

      <section className="pb-24 px-6 md:px-12">
        <div className="mx-auto max-w-3xl">
          <nav className="backdrop-blur-xl bg-[#0C111D]/65 border border-white/8 rounded-2xl p-6 mb-10">
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#D4AF37] mb-4">Contents</div>
            <ol className="space-y-1.5 text-sm text-white/60">
              {sections.map((s, i) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="hover:text-white transition-colors">
                    {i + 1}. {s.title}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <div className="space-y-10">
            {sections.map((s) => (
              <div key={s.id} id={s.id} className="scroll-mt-24">
                <h2 className="font-display text-2xl mb-4">{s.title}</h2>
                <div className="text-white/60 text-sm leading-relaxed whitespace-pre-line">{s.body}</div>
                <div className="h-px bg-white/5 mt-10" />
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/contact" className="text-sm text-[#D4AF37] hover:text-[#F2DE94] transition-colors">
              Questions? Contact us →
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
